import {Injectable, inject, NgZone} from '@angular/core';
import {onCLS, onINP, onLCP, type Metric} from 'web-vitals';

/**
 * Thin frontend telemetry hook for the PIDB UI.
 *
 * # What this service does
 *
 * 1. Wires the official `web-vitals` library (~5 kB gzipped) to surface the
 *    three "current" Core Web Vitals:
 *      - `LCP` (Largest Contentful Paint)
 *      - `INP` (Interaction to Next Paint — replaced `FID` in March 2024)
 *      - `CLS` (Cumulative Layout Shift)
 *    Observers are registered ONCE per session via `registerWebVitals()`,
 *    which `AppComponent.ngOnInit` calls on startup.
 *
 * 2. Exposes two custom hooks for ad-hoc instrumentation by feature code:
 *      - `trackEvent(name, props?)`     — discrete user/system events
 *      - `trackTiming(name, ms, props?)` — duration measurements
 *
 * # Reporting target
 *
 * For now, all three reporters emit a structured `console.info` line with a
 * `[telemetry]` prefix. External backend / Application Insights / Sentry
 * integration is intentionally deferred — wiring those requires either a
 * backend endpoint we haven't built or an external account we haven't set
 * up, and both are out of scope for Phase 11 #12. The console emit gives QA
 * a single grep-able prefix (`[telemetry]`) when reproducing performance
 * issues; switching the sink later is a single-method change.
 *
 * # Currently instrumented flows
 *
 * Listed so future contributors don't have to grep:
 *   - `file-upload-domain.service.ts`        — file-upload duration
 *     (`persistUploadedFile` HTTP+IndexedDB round-trip).
 *   - `file-display.component.ts`            — predict-all kick-off event
 *     (the predict-all flow's wall-clock duration is tracked downstream by
 *     `results.service.ts.showProcessProgress` poll loop).
 *   - `results.service.ts.showProcessProgress` — prediction duration from
 *     trigger to status==100 (or exception).
 *   - `results.service.ts.doValidateCardImmediate` — card validation HTTP
 *     duration.
 *
 * # Zone discipline
 *
 * Web Vitals observers fire inside the browser's PerformanceObserver
 * callback, NOT in an Angular zone task. Letting them re-enter the zone
 * would trigger unnecessary change-detection cycles (one per vital), so
 * registration happens inside `ngZone.runOutsideAngular()`. The custom
 * hooks (`trackEvent`/`trackTiming`) are called from already-in-zone code
 * paths and don't need that treatment.
 */
@Injectable({providedIn: 'root'})
export class TelemetryService {
    private ngZone = inject(NgZone);
    private vitalsRegistered = false;

    /**
     * Register Web Vitals observers ONCE per session. Idempotent — repeated
     * calls are no-ops. Called by `AppComponent.ngOnInit` on startup.
     */
    registerWebVitals(): void {
        if (this.vitalsRegistered) return;
        this.vitalsRegistered = true;
        this.ngZone.runOutsideAngular(() => {
            onLCP((m) => this.reportVital(m));
            onINP((m) => this.reportVital(m));
            onCLS((m) => this.reportVital(m));
        });
    }

    /** Record a custom event with optional structured properties. */
    trackEvent(name: string, props?: Record<string, unknown>): void {
        console.info('[telemetry] event:', name, props ?? {});
    }

    /** Record a duration measurement (rounded to whole milliseconds). */
    trackTiming(name: string, durationMs: number, props?: Record<string, unknown>): void {
        console.info('[telemetry] timing:', name, `${Math.round(durationMs)}ms`, props ?? {});
    }

    private reportVital(metric: Metric): void {
        console.info('[telemetry] vital:', metric.name, {
            value: Math.round(metric.value),
            rating: metric.rating,
            navigationType: metric.navigationType,
        });
    }
}
